import { useCallback } from 'react';

interface IGoogleReCaptchaOptions {
  siteKey: string;
}

interface GoogleReCaptchaV3HookReturn {
  executeReCaptcha: (action?: string) => Promise<string>;
}

type GoogleReCaptchaV3Hook = (
  options: IGoogleReCaptchaOptions
) => GoogleReCaptchaV3HookReturn;

const isBrowser = typeof window !== 'undefined';

interface GoogleReCaptcha {
  enterprise: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options?: { action: string }) => Promise<string>;
  };
}

export interface CustomWindow extends Window {
  grecaptcha: GoogleReCaptcha;
}

const getWindow = () =>
  isBrowser ? (window as unknown as CustomWindow) : undefined;

export const useGoogleReCaptchaV3: GoogleReCaptchaV3Hook = ({ siteKey }) => {
  const executeReCaptcha = useCallback(
    async (action = 'submit'): Promise<string> => {
      const window = getWindow();
      if (!window) {
        throw new Error('Running outside a web browser');
      }

      const { grecaptcha } = window;
      if (!grecaptcha) {
        throw new Error('Spam Protection is not available');
      }

      return new Promise((resolve) => {
        grecaptcha.enterprise.ready(() => {
          grecaptcha.enterprise.execute(siteKey, { action }).then((token) => {
            resolve(token);
          });
        });
      });
    },
    [siteKey]
  );

  return { executeReCaptcha };
};

export default useGoogleReCaptchaV3;
